import React, { useEffect, useMemo } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { Layers, Terminal, Brain, Rocket, Code, Database, Smartphone, Globe, LayoutDashboard, Lock, ShieldCheck } from 'lucide-react';
import { ParallaxBackground } from './ParallaxBackground';
import { useScroll } from 'framer-motion';

export const About: React.FC = () => {
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const springConfig = { damping: 40, stiffness: 200, mass: 0.5 };
  const mouseX = useSpring(x, springConfig);
  const mouseY = useSpring(y, springConfig);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      x.set((e.clientX / window.innerWidth) - 0.5);
      y.set((e.clientY / window.innerHeight) - 0.5);
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [x, y]);

  const bgX1 = useTransform(mouseX, [-0.5, 0.5], ["-50px", "50px"]);
  const bgY1 = useTransform(mouseY, [-0.5, 0.5], ["-50px", "50px"]);
  const bgX2 = useTransform(mouseX, [-0.5, 0.5], ["50px", "-50px"]);
  const bgY2 = useTransform(mouseY, [-0.5, 0.5], ["50px", "-50px"]);

  const { scrollY } = useScroll();

  const backgroundIcons = useMemo(() => [
    { Icon: Brain, top: '10%', left: '5%', size: 56, speed: 0.15, delay: 0, label: 'Brain - AI Strategy' },
    { Icon: Rocket, top: '20%', left: '85%', size: 48, speed: 0.2, delay: 0.2, label: 'Rocket - Growth' },
    { Icon: Layers, top: '70%', left: '10%', size: 64, speed: 0.1, delay: 0.4, label: 'Layers - Architecture' },
    { Icon: Terminal, top: '80%', left: '80%', size: 52, speed: 0.25, delay: 0.6, label: 'Terminal - Engineering' },
    { Icon: Code, top: '40%', left: '90%', size: 40, speed: 0.12, delay: 0.8, label: 'Code - Development' },
    { Icon: Database, top: '50%', left: '5%', size: 44, speed: 0.18, delay: 1.0, label: 'Database - Data' },
    { Icon: Smartphone, top: '5%', left: '40%', size: 36, speed: 0.08, delay: 1.2, label: 'Smartphone - Mobile' },
    { Icon: Globe, top: '90%', left: '45%', size: 42, speed: 0.3, delay: 1.4, label: 'Globe - Web' },
  ], []);

  return (
    <section className="pt-16 sm:pt-32 pb-16 sm:pb-24 text-white border-t border-[#d8aa68]/20 relative overflow-hidden bg-[#160b06]">
      {/* Rich Embossed Leather Grain & Ambient Horology Depth */}
      <div 
        className="absolute inset-0 pointer-events-none z-0 opacity-80"
        style={{
          backgroundColor: '#160b06',
          backgroundImage: `
            radial-gradient(ellipse at 50% 20%, rgba(85, 41, 20, 0.45) 0%, rgba(26, 12, 6, 0.95) 75%, #0d0603 100%),
            radial-gradient(circle at 15% 50%, rgba(139, 69, 19, 0.22) 0%, transparent 55%),
            radial-gradient(circle at 85% 65%, rgba(216, 170, 104, 0.12) 0%, transparent 50%),
            url("data:image/svg+xml,%3Csvg width='180' height='180' viewBox='0 0 180 180' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='leather'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.78' numOctaves='4' stitchTiles='stitch'/%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 0.18  0 0 0 0 0.09  0 0 0 0 0.04  0 0 0 0.55 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23leather)'/%3E%3C/svg%3E")
          `,
          backgroundBlendMode: 'normal, normal, normal, multiply'
        }}
      />

      {/* Saddle Stitch Top & Bottom Border Accents */}
      <div className="absolute top-0 inset-x-0 h-[2px] bg-repeat-x pointer-events-none opacity-40 z-10"
        style={{
          backgroundImage: 'linear-gradient(90deg, #d8aa68 0px, #d8aa68 6px, transparent 6px, transparent 12px)',
          backgroundSize: '12px 2px'
        }}
      />
      <div className="absolute bottom-0 inset-x-0 h-[2px] bg-repeat-x pointer-events-none opacity-40 z-10"
        style={{
          backgroundImage: 'linear-gradient(90deg, #d8aa68 0px, #d8aa68 6px, transparent 6px, transparent 12px)',
          backgroundSize: '12px 2px'
        }}
      />
      
      {/* Subtle Background Animation */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div 
            style={{ x: bgX1, y: bgY1 }}
            animate={{ 
                opacity: [0.03, 0.06, 0.03],
                scale: [1, 1.1, 1],
            }}
            transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
            className="absolute top-[-20%] right-[-10%] w-[800px] h-[800px] bg-[#8B4513]/15 rounded-full blur-[120px]"
        />
        <motion.div 
            style={{ x: bgX2, y: bgY2 }}
            animate={{ 
                opacity: [0.02, 0.05, 0.02],
                scale: [1, 1.2, 1],
            }}
            transition={{ duration: 15, repeat: Infinity, ease: "linear", delay: 2 }}
            className="absolute bottom-[-20%] left-[-10%] w-[600px] h-[600px] bg-[#4d2714]/20 rounded-full blur-[100px]"
        />
      </div>

      <ParallaxBackground 
        icons={backgroundIcons} 
        mouseX={mouseX} 
        mouseY={mouseY} 
        scrollY={scrollY} 
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 relative z-10">
        
        {/* Header Section */}
        <div className="max-w-4xl mx-auto">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="flex flex-col items-center text-center lg:items-start lg:text-left"
            >
                <span className="text-[#d8aa68] font-mono text-xs sm:text-sm uppercase tracking-widest mb-4 block">Who Am I?</span>
                <h2 className="text-3xl sm:text-5xl md:text-7xl font-display font-bold tracking-tight text-white mb-6 sm:mb-8 uppercase leading-[1.1]">
                    I Design, Architect, Build, <br className="hidden sm:block" />
                    <span className="text-gray-500">Scale Viable Products.</span>
                </h2>
                <p className="text-lg sm:text-xl text-gray-300 font-light leading-relaxed mb-6 sm:mb-8">
                    I am a Venture Builder, Product Architect, and Growth Strategist with over 8 years of experience building digital products that scale. As the founder of PopingUp, I've lived the entire lifecycle of a product - from the first line of code to global Product deployments.
                </p>
                <p className="text-left w-full text-base sm:text-lg text-gray-300 font-sans leading-relaxed mb-6 border-l-2 border-[#d8aa68] pl-4 sm:pl-5 py-2.5 bg-gradient-to-r from-[#d8aa68]/15 via-[#4d2714]/15 to-transparent rounded-r-xl shadow-[0_4px_20px_rgba(0,0,0,0.3)]">
                    <span className="text-[#ffe0a3] font-semibold block mb-1.5 font-mono text-xs uppercase tracking-widest">
                        ⚡ Commercial Velocity & Growth Architecture
                    </span>
                    I don’t just write code—I engineer <span className="text-white font-semibold">high-converting revenue engines</span> that command market share. By uniting deep-stack technical architecture with aggressive go-to-market growth strategy, I turn high-potential concepts into scalable, profitable digital ventures that drive rapid customer acquisition, reduce churn, and deliver measurable ROI.
                </p>

                {/* Core Systems I Build & Scale */}
                <div className="w-full mb-8 grid grid-cols-1 sm:grid-cols-2 gap-3.5 text-left">
                  <div className="p-4 rounded-xl bg-white/[0.03] border border-white/10 hover:border-[#d8aa68]/50 transition-all group shadow-sm">
                    <div className="flex items-center gap-2.5 mb-1.5">
                      <div className="p-1.5 rounded-lg bg-[#381b0e]/80 text-[#ffd885] border border-[#d8aa68]/30">
                        <Smartphone size={16} />
                      </div>
                      <h4 className="text-white font-mono font-bold text-xs sm:text-sm uppercase tracking-wider group-hover:text-[#ffe0a3] transition-colors">
                        Web &amp; Mobile Apps
                      </h4>
                    </div>
                    <p className="text-xs text-gray-400 font-light leading-relaxed">
                      High-performance iOS, Android, and responsive web applications engineered with clean architectures, fluid motion, and offline-first capabilities.
                    </p>
                  </div>

                  <div className="p-4 rounded-xl bg-white/[0.03] border border-white/10 hover:border-[#d8aa68]/50 transition-all group shadow-sm">
                    <div className="flex items-center gap-2.5 mb-1.5">
                      <div className="p-1.5 rounded-lg bg-[#381b0e]/80 text-[#ffd885] border border-[#d8aa68]/30">
                        <LayoutDashboard size={16} />
                      </div>
                      <h4 className="text-white font-mono font-bold text-xs sm:text-sm uppercase tracking-wider group-hover:text-[#ffe0a3] transition-colors">
                        Admin Dashboards
                      </h4>
                    </div>
                    <p className="text-xs text-gray-400 font-light leading-relaxed">
                      Custom operations command centers, real-time telemetry, user management consoles, and live metrics that eliminate organizational blind spots.
                    </p>
                  </div>

                  <div className="p-4 rounded-xl bg-white/[0.03] border border-white/10 hover:border-[#d8aa68]/50 transition-all group shadow-sm">
                    <div className="flex items-center gap-2.5 mb-1.5">
                      <div className="p-1.5 rounded-lg bg-[#381b0e]/80 text-[#ffd885] border border-[#d8aa68]/30">
                        <Lock size={16} />
                      </div>
                      <h4 className="text-white font-mono font-bold text-xs sm:text-sm uppercase tracking-wider group-hover:text-[#ffe0a3] transition-colors">
                        Secure Login Portals
                      </h4>
                    </div>
                    <p className="text-xs text-gray-400 font-light leading-relaxed">
                      Client gateways, multi-tenant onboarding hubs, enterprise SSO, biometric auth, and frictionless passwordless login flows.
                    </p>
                  </div>

                  <div className="p-4 rounded-xl bg-white/[0.03] border border-white/10 hover:border-[#d8aa68]/50 transition-all group shadow-sm">
                    <div className="flex items-center gap-2.5 mb-1.5">
                      <div className="p-1.5 rounded-lg bg-[#381b0e]/80 text-[#ffd885] border border-[#d8aa68]/30">
                        <ShieldCheck size={16} />
                      </div>
                      <h4 className="text-white font-mono font-bold text-xs sm:text-sm uppercase tracking-wider group-hover:text-[#ffe0a3] transition-colors">
                        Enterprise Security &amp; RBAC
                      </h4>
                    </div>
                    <p className="text-xs text-gray-400 font-light leading-relaxed">
                      Fine-grained role-based access controls, zero-trust architecture, encrypted data pipelines, API rate-limiting, and compliance audit logging.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row flex-wrap gap-4 justify-center lg:justify-start w-full">
                    <button 
                        onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                        className="relative w-full sm:w-auto px-8 py-3.5 rounded-full overflow-hidden transition-all duration-300 group shadow-[0_6px_20px_rgba(0,0,0,0.8),inset_0_1px_2px_rgba(255,255,255,0.25)] border border-[#d8aa68]/50 hover:border-[#ffe0a3]/80 hover:shadow-[0_8px_25px_rgba(216,170,104,0.3)] text-center"
                        style={{
                          background: 'linear-gradient(180deg, #4d2714 0%, #381b0e 38%, #241108 72%, #140803 100%)'
                        }}
                    >
                        <span className="absolute inset-[2px] rounded-full border border-dashed border-[#d8aa68]/30 pointer-events-none group-hover:border-[#ffe0a3]/60 transition-colors"></span>
                        <span className="relative z-10 text-xs font-mono uppercase tracking-widest font-bold text-[#ffe0a3] group-hover:text-white transition-colors">
                            Work With Me
                        </span>
                    </button>
                    <button 
                        onClick={() => document.getElementById('capabilities')?.scrollIntoView({ behavior: 'smooth' })}
                        className="w-full sm:w-auto px-8 py-3.5 border border-white/20 text-white font-mono font-bold text-xs uppercase tracking-widest hover:border-[#d8aa68]/70 hover:text-[#ffe0a3] hover:bg-white/[0.06] transition-all rounded-full text-center"
                    >
                        See Capabilities
                    </button>
                </div>
            </motion.div>

            <div className="grid grid-cols-2 gap-6 sm:gap-8 border-t border-white/10 pt-8 mt-12 text-center lg:text-left max-w-md">
                <div>
                    <div className="text-2xl sm:text-3xl font-bold text-white mb-1">8+ Years</div>
                    <div className="text-xs sm:text-sm text-gray-500 font-mono uppercase">Experience</div>
                </div>
                <div>
                    <div className="text-2xl sm:text-3xl font-bold text-white mb-1">Founder</div>
                    <div className="text-xs sm:text-sm text-gray-500 font-mono uppercase">of PopingUp</div>
                </div>
            </div>
        </div>



      </div>
    </section>
  );
};
